import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useTour } from "@/context/TourContext";
import { WalkthroughDialog } from "@/components/tour/WalkthroughDialog";
import { useVault } from "@/context/VaultContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ThemeToggle } from "@/components/ThemeToggle";
import { EmailVerifyBanner } from "@/components/EmailVerifyBanner";
import { RecoveryFollowUpDialog } from "@/components/passord/RecoveryFollowUpDialog";
import { ControlLogo, ControlMark } from "@/components/ControlLogo";
import { Bell, LayoutDashboard, FileText, Upload, Building2, Tags, CalendarRange, Settings, LogOut, Menu, Search, ChevronRight, ShieldCheck, PencilLine, Users, Eye, UserCog, ArrowLeftRight, Check, Inbox, Receipt, Contact, Package, BadgeCheck, Plus, KeyRound, Lock, Unlock, LockKeyhole, Share2, HeartPulse, BellRing, Briefcase, ChevronsUpDown, User, Repeat, Smartphone, Archive, LifeBuoy, MailCheck, House } from "lucide-react";
import { ENTITY_SHORT } from "@/lib/salesConstants";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/lib/format";

export const MODULE_KEY = "rp_module";

export const MODULES = {
  regninger: { label: "Regninger", icon: Inbox, home: "/", description: "Inngående regninger" },
  faktura: { label: "Faktura", icon: Receipt, home: "/faktura", description: "Utgående fakturering" },
  passord: { label: "Passord", icon: KeyRound, home: "/passord", description: "Kryptert hvelv" },
  husholdning: { label: "Regnskap", icon: House, home: "/regnskap", description: "Regnskap og husholdning" },
};

/** Etikett for en modul. Regnskap-modulen heter «Husholdning» når virksomhetens kontoplan er husholdningsregnskap. */
export const moduleLabel = (key, chartPlan) => (key === "husholdning" ? (chartPlan === "husholdning" ? "Husholdning" : "Regnskap") : MODULES[key]?.label || key);

/** Moduler en fullmektig (act-as) kan bruke på vegne av eier – samme regler som for Regninger. */
export const DELEGATE_MODULES = ["regninger", "husholdning"];

/** Moduler som er aktivert for en virksomhet (alle når ukjent). */
export const enabledModules = (workspace) => Object.keys(MODULES).filter((k) => (workspace?.modules ? workspace.modules[k] !== false : true));

/** Sider som er felles for alle moduler – beholder sist valgte modul i sidemenyen. */
export const SHARED_PATHS = ["/profil", "/virksomheter", "/innstillinger", "/varsler", "/familie", "/hjelp"];

/** Bilagsbanken er felles for Regninger og Faktura (ikke Passord). */
export const isSharedPath = (pathname) => SHARED_PATHS.includes(pathname) || pathname === "/bilag" || pathname.startsWith("/bilag/");

export function detectModule(pathname) {
  if (pathname.startsWith("/faktura")) return "faktura";
  if (pathname.startsWith("/passord")) return "passord";
  if (pathname.startsWith("/regnskap")) return "husholdning";
  if (pathname === "/bilag" || pathname.startsWith("/bilag/")) {
    return localStorage.getItem(MODULE_KEY) === "faktura" ? "faktura" : "regninger";
  }
  if (SHARED_PATHS.includes(pathname)) {
    const stored = localStorage.getItem(MODULE_KEY);
    return stored === "faktura" || stored === "passord" || stored === "husholdning" ? stored : "regninger";
  }
  return "regninger";
}

const buildHusholdningNav = ({ canWrite, isOwner, chartPlan }) => [
  {
    label: null,
    items: [
      { to: "/regnskap", label: "Oversikt", icon: LayoutDashboard, end: true, testId: "sidebar-household-overview-link" },
      { to: "/regnskap/transaksjoner", label: "Transaksjoner", icon: ArrowLeftRight, testId: "sidebar-household-transactions-link" },
      { to: "/regnskap/aar", label: "Årsoversikt", icon: CalendarRange, testId: "sidebar-household-year-link" },
    ],
  },
  ...(canWrite ? [{
    label: "Legg til",
    items: [
      { to: "/regnskap/transaksjoner?ny=1", label: "Ny post", icon: PencilLine, testId: "sidebar-household-new-entry-link" },
    ],
  }] : []),
  {
    label: "Organiser",
    items: [
      { to: "/regnskap/kategorier", label: chartPlan === "husholdning" ? "Kategoriplan" : "Kontoplan", icon: Tags, testId: "sidebar-household-categories-link" },
    ],
  },
  {
    label: "Konto",
    items: [
      { to: "/varsler", label: "Varsler", icon: Bell, testId: "sidebar-notifications-link" },
      ...(isOwner ? [
        { to: "/profil", label: "Min profil", icon: BadgeCheck, testId: "sidebar-profile-link" },
        { to: "/virksomheter", label: "Virksomheter", icon: Briefcase, testId: "sidebar-workspaces-link" },
        { to: "/familie", label: "Familie og fullmakt", icon: Users, testId: "sidebar-family-link" },
        { to: "/innstillinger", label: "Innstillinger", icon: Settings, testId: "sidebar-settings-link" },
      ] : []),
      { to: "/hjelp", label: "Hjelp", icon: LifeBuoy, testId: "sidebar-help-link" },
    ],
  },
];

const buildPassordNav = () => [
  {
    label: null,
    items: [
      { to: "/passord", label: "Hvelv", icon: Lock, end: true, testId: "passord-nav-hvelv" },
      { to: "/passord/delt", label: "Delt med meg", icon: Share2, testId: "passord-nav-delt" },
      { to: "/passord/helse", label: "Passordhelse", icon: HeartPulse, testId: "passord-nav-helse" },
    ],
  },
  {
    label: "Konto",
    items: [
      { to: "/passord/innstillinger", label: "Hvelvinnstillinger", icon: ShieldCheck, testId: "passord-nav-innstillinger" },
      { to: "/profil", label: "Min profil", icon: BadgeCheck, testId: "sidebar-profile-link" },
      { to: "/virksomheter", label: "Virksomheter", icon: Briefcase, testId: "sidebar-workspaces-link" },
      { to: "/varsler", label: "Varsler", icon: Bell, testId: "sidebar-notifications-link" },
      { to: "/innstillinger", label: "Innstillinger", icon: Settings, testId: "sidebar-settings-link" },
      { to: "/hjelp", label: "Hjelp", icon: LifeBuoy, testId: "sidebar-help-link" },
    ],
  },
];

const buildRegningerNav = ({ canWrite, isOwner }) => [
  {
    label: null,
    items: [
      { to: "/", label: "Oversikt", icon: LayoutDashboard, end: true, testId: "sidebar-dashboard-link" },
      { to: "/fakturaer", label: "Regninger", icon: FileText, testId: "sidebar-invoices-link" },
      { to: "/maanedsoversikt", label: "Månedsoversikt", icon: CalendarRange, testId: "sidebar-monthly-link" },
    ],
  },
  ...(canWrite ? [{
    label: "Legg til",
    items: [
      { to: "/last-opp", label: "Last opp", icon: Upload, testId: "sidebar-upload-link" },
      { to: "/registrer", label: "Registrer manuelt", icon: PencilLine, testId: "sidebar-manual-link" },
    ],
  }] : []),
  {
    label: "Organiser",
    items: [
      { to: "/bilag", label: "Bilagsbank", icon: Archive, testId: "sidebar-bilag-link" },
      { to: "/leverandorer", label: "Leverandører", icon: Building2, testId: "sidebar-suppliers-link" },
      { to: "/kategorier", label: "Kategorier", icon: Tags, testId: "sidebar-categories-link" },
    ],
  },
  {
    label: "Konto",
    items: [
      { to: "/varsler", label: "Varsler", icon: Bell, testId: "sidebar-notifications-link" },
      ...(isOwner ? [
        { to: "/profil", label: "Min profil", icon: BadgeCheck, testId: "sidebar-profile-link" },
        { to: "/virksomheter", label: "Virksomheter", icon: Briefcase, testId: "sidebar-workspaces-link" },
        { to: "/familie", label: "Familie og fullmakt", icon: Users, testId: "sidebar-family-link" },
        { to: "/innstillinger", label: "Innstillinger", icon: Settings, testId: "sidebar-settings-link" },
      ] : []),
      { to: "/hjelp", label: "Hjelp", icon: LifeBuoy, testId: "sidebar-help-link" },
    ],
  },
];

const buildFakturaNav = () => [
  {
    label: null,
    items: [
      { to: "/faktura", label: "Oversikt", icon: LayoutDashboard, end: true, testId: "sidebar-sales-dashboard-link" },
      { to: "/faktura/fakturaer", label: "Fakturaer", icon: Receipt, testId: "sidebar-sales-invoices-link" },
      { to: "/faktura/purringer", label: "Purringer", icon: BellRing, testId: "sidebar-sales-reminders-link" },
      { to: "/faktura/gjentakende", label: "Gjentakende", icon: Repeat, testId: "sidebar-sales-recurring-link" },
      { to: "/faktura/epostlogg", label: "E-postlogg", icon: MailCheck, testId: "sidebar-sales-email-log-link" },
    ],
  },
  {
    label: "Legg til",
    items: [
      { to: "/faktura/fakturaer/ny", label: "Ny faktura", icon: Plus, testId: "sidebar-sales-new-invoice-link" },
    ],
  },
  {
    label: "Register",
    items: [
      { to: "/faktura/kunder", label: "Kunder", icon: Contact, testId: "sidebar-sales-customers-link" },
      { to: "/faktura/produkter", label: "Produkter og tjenester", icon: Package, testId: "sidebar-sales-products-link" },
      { to: "/bilag", label: "Bilagsbank", icon: Archive, testId: "sidebar-bilag-link" },
    ],
  },
  {
    label: "Konto",
    items: [
      { to: "/profil", label: "Firmaprofil", icon: BadgeCheck, testId: "sidebar-profile-link" },
      { to: "/faktura/betaling", label: "Betaling med Vipps", icon: Smartphone, testId: "sidebar-sales-payments-link" },
      { to: "/virksomheter", label: "Virksomheter", icon: Briefcase, testId: "sidebar-workspaces-link" },
      { to: "/varsler", label: "Varsler", icon: Bell, testId: "sidebar-notifications-link" },
      { to: "/innstillinger", label: "Innstillinger", icon: Settings, testId: "sidebar-settings-link" },
      { to: "/hjelp", label: "Hjelp", icon: LifeBuoy, testId: "sidebar-help-link" },
    ],
  },
];

const Brand = ({ compact = false, to = "/" }) => (
  <Link to={to} className="flex items-center px-1" data-testid="brand-link" aria-label="Controlle – til oversikten">
    {compact ? <span className="text-primary"><ControlMark size={30} /></span> : <ControlLogo size={30} tagline="Regninger og faktura, ett sted" />}
  </Link>
);

const ModuleSwitcher = ({ module, onChange, className, enabled = Object.keys(MODULES), chartPlan }) => (
  <div className={cn("grid gap-1 rounded-xl border bg-secondary p-1", enabled.length >= 4 ? "grid-cols-2" : enabled.length === 3 ? "grid-cols-3" : enabled.length === 2 ? "grid-cols-2" : "grid-cols-1", className)} role="tablist" aria-label="Modul" data-testid="module-switcher">
    {Object.entries(MODULES).filter(([key]) => enabled.includes(key)).map(([key, m]) => {
      const active = module === key;
      return (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(key)}
          className={cn(
            "flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 rounded-lg px-2 py-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          )}
          data-testid={`module-switcher-${key}`}
        >
          <m.icon style={{ width: 15, height: 15 }} /> {moduleLabel(key, chartPlan)}
        </button>
      );
    })}
  </div>
);

/** Virksomhetsvelger i toppbaren: aktiv virksomhet + bytte + «Administrer virksomheter». */
const WorkspaceSwitcher = ({ workspaces, active, onSwitch, onManage }) => {
  if (!active) return null;
  const Icon = active.entity_type === "privat" ? User : Building2;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="min-w-0 max-w-[170px] sm:max-w-[240px] gap-2 px-2.5 bg-background" aria-label={`Aktiv virksomhet: ${active.name}`} data-testid="workspace-switcher-button">
          <Icon className="h-4 w-4 text-primary shrink-0" />
          <span className="truncate min-w-0 text-sm font-medium" data-testid="workspace-active-name">{active.name || "Uten navn"}</span>
          <span className="hidden sm:inline rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{ENTITY_SHORT[active.entity_type] || active.entity_type}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 bg-card" data-testid="workspace-switcher-menu">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" /> Virksomhet</DropdownMenuLabel>
        {workspaces.map((w) => {
          const WIcon = w.entity_type === "privat" ? User : Building2;
          const isActive = w.id === active.id;
          return (
            <DropdownMenuItem key={w.id} onClick={() => (isActive ? null : onSwitch(w))} className="gap-2" data-testid={`workspace-switch-${w.id}`}>
              <WIcon className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block truncate text-sm">{w.name || "Uten navn"}</span>
                <span className="block text-[11px] text-muted-foreground">{ENTITY_SHORT[w.entity_type] || w.entity_type}{w.org_number ? ` · ${w.org_number}` : ""}{w.is_primary ? " · primær" : ""}</span>
              </span>
              {isActive ? <Check className="h-4 w-4 text-primary shrink-0" /> : null}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onManage} data-testid="workspace-manage-link"><Settings className="h-4 w-4 mr-2" /> Administrer virksomheter</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const NavItems = ({ onNavigate, unread, pending, groups, badges = {} }) => (
  <nav className="flex flex-col gap-5" aria-label="Hovedmeny">
    {groups.map((g, gi) => (
      <div key={gi} className="flex flex-col gap-0.5">
        {g.label ? <p className="px-3 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">{g.label}</p> : null}
        {g.items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={item.testId}
            className={({ isActive }) =>
              cn(
                "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                isActive ? "bg-accent text-accent-foreground" : "text-foreground/75 hover:bg-secondary hover:text-foreground"
              )
            }
          >
            <item.icon style={{ width: 18, height: 18 }} className="shrink-0" />
            <span className="flex-1">{item.label}</span>
            {item.to === "/varsler" && unread > 0 ? (
              <span className="rounded-full bg-primary text-primary-foreground text-[11px] px-1.5 py-0.5 min-w-[20px] text-center tnum" data-testid="sidebar-unread-count">{unread}</span>
            ) : null}
            {item.to === "/familie" && pending > 0 ? (
              <span className="rounded-full bg-primary text-primary-foreground text-[11px] px-1.5 py-0.5 min-w-[20px] text-center tnum" data-testid="sidebar-pending-invites">{pending}</span>
            ) : null}
            {badges[item.to] > 0 ? (
              <span className={cn("rounded-full text-[11px] px-1.5 py-0.5 min-w-[20px] text-center tnum", badges[`${item.to}:tone`] === "flag" ? "status-avvist border" : "bg-primary text-primary-foreground")} data-testid={`sidebar-badge-${item.testId}`}>{badges[item.to]}</span>
            ) : null}
          </NavLink>
        ))}
      </div>
    ))}
  </nav>
);

const reduceMotion = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const AppShell = () => {
  const { user, logout, actAs, switchActAs, canWrite, isOwner, workspaces, activeWorkspace, switchWorkspace } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const modulesOn = isOwner ? enabledModules(activeWorkspace) : DELEGATE_MODULES;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const vault = useVault();
  const detected = isOwner ? detectModule(location.pathname) : (location.pathname.startsWith("/regnskap") ? "husholdning" : "regninger");
  const module = modulesOn.includes(detected) ? detected : (modulesOn[0] || "regninger");
  // Kontoplan for virksomheten (eier) eller eierens primære virksomhet (fullmektig) – styrer etiketten Husholdning/Regnskap
  const chartPlan = isOwner ? activeWorkspace?.chart_plan : actAs?.owner_chart_plan;

  // Animert gjennomgang første gang appen / en modul / Bilagsbanken åpnes (én om gangen, i rekkefølge)
  const tour = useTour();
  const onBilagPath = location.pathname === "/bilag" || location.pathname.startsWith("/bilag/");
  useEffect(() => {
    if (!tour.loaded || tour.active) return undefined;
    const key = !tour.seen.start ? "start" : onBilagPath && !tour.seen.bilagsbank ? "bilagsbank" : !tour.seen[module] ? module : null;
    if (!key) return undefined;
    const t = setTimeout(() => tour.maybeAutoStart(key), 700);
    return () => clearTimeout(t);
  }, [tour.loaded, tour.active, tour.seen, module, onBilagPath]); // eslint-disable-line react-hooks/exhaustive-deps
  // Deaktivert modul for aktiv virksomhet → send til første aktive modul
  useEffect(() => {
    if (!isOwner || modulesOn.includes(detected)) return;
    if (isSharedPath(location.pathname)) return;
    const target = MODULES[modulesOn[0] || "regninger"].home;
    toast.info(`Modulen ${moduleLabel(detected, activeWorkspace?.chart_plan)} er ikke aktivert for ${activeWorkspace?.name || "denne virksomheten"}.`, { id: "module-off", description: "Du kan slå den på under Virksomheter." });
    navigate(target, { replace: true });
  }, [isOwner, detected, modulesOn, location.pathname, navigate, activeWorkspace?.name]);
  const isFaktura = module === "faktura";
  const isPassord = module === "passord";
  const isHusholdning = module === "husholdning";
  const brandHome = MODULES[modulesOn[0] || "regninger"].home;
  const groups = isFaktura ? buildFakturaNav() : isPassord ? buildPassordNav() : isHusholdning ? buildHusholdningNav({ canWrite, isOwner, chartPlan }) : buildRegningerNav({ canWrite, isOwner });
  const delegations = user?.delegations_received || [];
  // Badge for Faktura → Purringer (antall fakturaer klare for purring)
  const [badges, setBadges] = useState({});
  useEffect(() => {
    if (!isFaktura || !isOwner) return;
    let cancelled = false;
    const refresh = () => {
      api.get("/sales/reminders/suggestions", { skipActAs: true })
        .then((r) => { if (!cancelled) setBadges((b) => ({ ...b, "/faktura/purringer": r.data.count || 0 })); })
        .catch(() => {});
      api.get("/sales/recurring/pending-count", { skipActAs: true })
        .then((r) => { if (!cancelled) setBadges((b) => ({ ...b, "/faktura/gjentakende": r.data.count || 0 })); })
        .catch(() => {});
    };
    refresh();
    // Oppdateres umiddelbart etter purring/betaling/godkjenning (sendes fra Faktura-sidene)
    window.addEventListener("rp:refresh-reminders", refresh);
    window.addEventListener("rp:refresh-recurring", refresh);
    return () => { cancelled = true; window.removeEventListener("rp:refresh-reminders", refresh); window.removeEventListener("rp:refresh-recurring", refresh); };
  }, [isFaktura, isOwner, location.pathname]);

  useEffect(() => {
    localStorage.setItem(MODULE_KEY, module);
  }, [module]);

  // Lekkasjesjekk: badge på «Passordhelse» + ett varsel per sjekk-runde når passord er funnet i lekkasjer
  const breach = vault.breach;
  const notifiedRun = useRef(0);
  useEffect(() => {
    if (breach?.status !== "done" || !breach.leaked || breach.runId === notifiedRun.current) return;
    notifiedRun.current = breach.runId;
    if (location.pathname === "/passord/helse") return;
    toast.warning(`${breach.leaked} ${breach.leaked === 1 ? "passord er" : "passord er"} funnet i kjente datalekkasjer`, {
      id: "vault-breach",
      description: "Bytt dem så snart som mulig – og overalt du har brukt det samme passordet.",
      duration: 12000,
      action: { label: "Se Passordhelse", onClick: () => navigate("/passord/helse") },
    });
  }, [breach?.status, breach?.leaked, breach?.runId, navigate, location.pathname]);
  const navBadges = useMemo(() => (isPassord && breach?.leaked ? { ...badges, "/passord/helse": breach.leaked, "/passord/helse:tone": "flag" } : badges), [badges, isPassord, breach?.leaked]);

  const loadNotifs = async () => {
    try {
      const res = await api.get("/notifications", { params: { limit: 8 } });
      setNotifs(res.data);
    } catch {
      /* stille */
    }
  };

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 60000);
    const onRefresh = () => loadNotifs();
    window.addEventListener("rp:refresh-notifications", onRefresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("rp:refresh-notifications", onRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const changeModule = (key) => {
    localStorage.setItem(MODULE_KEY, key);
    setOpen(false);
    navigate(MODULES[key].home);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`${isFaktura ? "/faktura/fakturaer" : isPassord ? "/passord" : isHusholdning ? "/regnskap/transaksjoner" : "/fakturaer"}?q=${encodeURIComponent(q.trim())}`);
  };

  const initials = (user?.full_name || user?.email || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const onUploadPage = location.pathname === "/last-opp";
  const onBilagDetail = /^\/bilag\/[^/]+$/.test(location.pathname);
  const onBilag = location.pathname === "/bilag" || onBilagDetail;
  const onNewInvoice = location.pathname === "/faktura/fakturaer/ny" || /^\/faktura\/fakturaer\/[^/]+\/rediger$/.test(location.pathname);
  const onHouseholdNew = location.pathname === "/regnskap/transaksjoner" && location.search.includes("ny=1");
  const hideFab = onBilag || (isPassord
    ? vault.status !== "unlocked" || location.pathname !== "/passord"
    : isFaktura ? onNewInvoice || /^\/faktura\/fakturaer\/[^/]+$/.test(location.pathname)
      : isHusholdning ? onHouseholdNew || !canWrite
        : onUploadPage || /^\/fakturaer\/[^/]+$/.test(location.pathname) || !canWrite);
  const primaryAction = isPassord
    ? { to: "/passord?ny=1", label: "Nytt passord", icon: Plus, hidden: vault.status !== "unlocked", testId: "topbar-new-entry-button" }
    : isFaktura
      ? { to: "/faktura/fakturaer/ny", label: "Ny faktura", icon: Plus, hidden: onNewInvoice, testId: "topbar-new-invoice-button" }
      : isHusholdning
        ? { to: "/regnskap/transaksjoner?ny=1", label: "Ny post", icon: Plus, hidden: onHouseholdNew || !canWrite, testId: "topbar-new-household-entry-button" }
        : { to: "/last-opp", label: "Last opp", icon: Upload, hidden: onUploadPage || !canWrite, testId: "topbar-upload-button" };

  const ContextSwitcher = delegations.length ? (
    <>
      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal flex items-center gap-1.5"><ArrowLeftRight className="h-3.5 w-3.5" /> Vis regninger og regnskap for</DropdownMenuLabel>
      <DropdownMenuItem onClick={() => actAs && switchActAs(null)} data-testid="switch-own-account">
        <span className="flex-1">Meg selv</span>{!actAs ? <Check className="h-4 w-4 text-primary" /> : null}
      </DropdownMenuItem>
      {delegations.map((d) => (
        <DropdownMenuItem key={d.delegation_id} onClick={() => (actAs?.owner_id === d.owner_id ? null : switchActAs(d))} data-testid={`switch-act-as-${d.owner_id}`}>
          <span className="flex-1 truncate">{d.owner_name}</span>
          <span className="text-[11px] text-muted-foreground mr-2">{d.role === "handtering" ? "Håndtering" : "Innsyn"}</span>
          {actAs?.owner_id === d.owner_id ? <Check className="h-4 w-4 text-primary" /> : null}
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
    </>
  ) : null;

  const SidebarInner = ({ onNavigate }) => (
    <>
      {modulesOn.length > 1 ? <ModuleSwitcher module={module} onChange={changeModule} enabled={modulesOn} chartPlan={chartPlan} /> : null}
      <NavItems onNavigate={onNavigate} unread={notifs.unread} pending={isOwner ? user?.pending_invitations : 0} groups={groups} badges={navBadges} />
    </>
  );

  return (
    <div className="min-h-screen app-canvas flex">
      <aside className="hidden lg:flex w-[264px] shrink-0 flex-col border-r bg-card px-4 py-5 gap-6 sticky top-0 h-screen overflow-y-auto">
        <Brand to={brandHome} />
        <SidebarInner />
        <div className="mt-auto flex items-start gap-2 px-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p>Kun du har tilgang til dataene dine. Automatisk utlogging etter {user?.auto_logout_minutes || 15} min.</p>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/90">
          <div className="flex items-center gap-3 px-4 sm:px-6 lg:px-8 h-16">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Åpne meny" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[288px] bg-card p-4 overflow-y-auto">
                <SheetTitle className="sr-only">Meny</SheetTitle>
                <div className="mb-5"><Brand to={brandHome} /></div>
                <div className="space-y-5"><SidebarInner onNavigate={() => setOpen(false)} /></div>
              </SheetContent>
            </Sheet>
            {/* På små skjermer erstatter virksomhetsvelgeren logoen (logoen ligger i menyen) */}
            <div className={cn("lg:hidden flex items-center gap-2", isOwner && "hidden sm:flex")}><Brand compact to={brandHome} /></div>
            {isOwner ? (
              <div className="min-w-0 shrink">
                <WorkspaceSwitcher workspaces={workspaces} active={activeWorkspace} onSwitch={(w) => switchWorkspace(w.id, MODULES[enabledModules(w)[0] || "regninger"].home)} onManage={() => navigate("/virksomheter")} />
              </div>
            ) : null}
            <Badge variant="outline" className="hidden sm:inline-flex rounded-full px-2.5 py-0.5 text-xs bg-accent text-accent-foreground border-transparent font-medium" data-testid="module-badge">{moduleLabel(module, chartPlan)}</Badge>
            {isPassord && vault.status === "unlocked" ? (
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full border bg-accent text-accent-foreground px-2 py-0.5 text-xs" data-testid="vault-lock-indicator-unlocked"><Unlock className="h-3 w-3" /> Åpen</span>
            ) : isPassord && vault.status === "locked" ? (
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full border bg-secondary text-muted-foreground px-2 py-0.5 text-xs" data-testid="vault-lock-indicator-locked"><Lock className="h-3 w-3" /> Låst</span>
            ) : null}

            <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-md items-center relative" role="search">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={isFaktura ? "Søk etter kunde, fakturanr eller referanse…" : isPassord ? "Søk i hvelvet etter navn, brukernavn eller adresse…" : isHusholdning ? "Søk i transaksjoner etter leverandør, kunde eller tekst…" : "Søk etter leverandør, KID eller beløp…"} className="pl-9 bg-background border-transparent focus-visible:border-input" data-testid="topbar-search-input" aria-label="Søk" />
            </form>

            <div className="ml-auto flex items-center gap-1.5">
              {isPassord && vault.status === "unlocked" ? (
                <Button variant="secondary" size="sm" onClick={() => vault.lock("manual")} data-testid="vault-lock-now-button"><LockKeyhole className="h-4 w-4 mr-1.5" /> Lås</Button>
              ) : null}
              {!primaryAction.hidden && (
                <Button asChild variant="outline" className="hidden sm:inline-flex" data-testid={primaryAction.testId}>
                  <Link to={primaryAction.to}><primaryAction.icon className="h-4 w-4 mr-2" /> {primaryAction.label}</Link>
                </Button>
              )}
              <ThemeToggle />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" aria-label="Varsler" data-testid="notification-bell-button">
                    <Bell className="h-5 w-5" />
                    {notifs.unread > 0 ? (
                      <span className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] px-1 rounded-full bg-primary text-primary-foreground text-[11px] flex items-center justify-center tnum ring-2 ring-card" data-testid="notification-unread-badge">{notifs.unread > 99 ? "99+" : notifs.unread}</span>
                    ) : null}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[360px] p-0 bg-card">
                  <div className="flex items-center justify-between px-4 py-3 border-b">
                    <p className="font-medium">Varsler</p>
                    <Button variant="link" size="sm" className="h-auto p-0" asChild><Link to="/varsler" data-testid="notifications-see-all">Se alle</Link></Button>
                  </div>
                  <div className="max-h-[360px] overflow-auto">
                    {notifs.items.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground">Ingen varsler ennå.</p>
                    ) : (
                      notifs.items.map((n) => (
                        <button
                          key={n.id}
                          type="button"
                          onClick={async () => {
                            if (canWrite) { try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ } }
                            loadNotifs();
                            if (n.link) navigate(n.link);
                            else if (n.invoice_id) navigate(`/fakturaer/${n.invoice_id}`);
                            else if (n.type?.startsWith("fullmakt")) navigate("/familie");
                          }}
                          className={cn("w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-secondary transition-colors", !n.read && "bg-accent/40")}
                          data-testid={`notification-item-${n.id}`}
                        >
                          <p className="text-sm font-medium">{n.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                          <p className="text-[11px] text-muted-foreground mt-1">{formatDateTime(n.created_at)}</p>
                        </button>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 px-2" data-testid="profile-menu-button">
                    <span className={cn("h-8 w-8 rounded-full text-xs font-semibold flex items-center justify-center", actAs ? "actas-banner border" : "bg-primary text-primary-foreground")}>{actAs ? <UserCog className="h-4 w-4" /> : initials}</span>
                    <span className="hidden md:inline text-sm font-medium max-w-[160px] truncate">{actAs ? actAs.owner_name : user?.full_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 bg-card">
                  <DropdownMenuLabel>
                    <p className="font-medium">{user?.full_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {ContextSwitcher}
                  {isOwner && modulesOn.includes("passord") ? <DropdownMenuItem onClick={() => navigate("/passord")} data-testid="profile-menu-vault"><KeyRound className="h-4 w-4 mr-2" /> Passord</DropdownMenuItem> : null}
                  {isOwner ? <DropdownMenuItem onClick={() => navigate("/profil")} data-testid="profile-menu-profile"><BadgeCheck className="h-4 w-4 mr-2" /> Min profil</DropdownMenuItem> : null}
                  {isOwner ? <DropdownMenuItem onClick={() => navigate("/virksomheter")} data-testid="profile-menu-workspaces"><Briefcase className="h-4 w-4 mr-2" /> Virksomheter</DropdownMenuItem> : null}
                  {isOwner ? <DropdownMenuItem onClick={() => navigate("/familie")} data-testid="profile-menu-family"><Users className="h-4 w-4 mr-2" /> Familie og fullmakt</DropdownMenuItem> : null}
                  {isOwner ? <DropdownMenuItem onClick={() => navigate("/innstillinger")} data-testid="profile-menu-settings"><Settings className="h-4 w-4 mr-2" /> Innstillinger</DropdownMenuItem> : null}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => logout()} data-testid="logout-button"><LogOut className="h-4 w-4 mr-2" /> Logg ut</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {actAs ? (
            <div className="actas-banner border-t px-4 sm:px-6 lg:px-8 py-2 text-sm flex flex-wrap items-center gap-x-3 gap-y-1" role="status" data-testid="act-as-banner">
              {actAs.role === "handtering" ? <UserCog className="h-4 w-4 shrink-0" /> : <Eye className="h-4 w-4 shrink-0" />}
              <span>Du ser {isHusholdning ? (chartPlan === "husholdning" ? "husholdningsregnskapet" : "regnskapet") : "regningene"} til <strong>{actAs.owner_name}</strong> · {actAs.role === "handtering" ? "Håndtering: du kan bekrefte, redigere og føre poster, men ikke slette." : "Kun innsyn: du kan se, men ikke endre."}</span>
              <button type="button" onClick={() => switchActAs(null)} className="ml-auto underline font-medium hover:no-underline" data-testid="act-as-exit">Tilbake til mine egne</button>
            </div>
          ) : null}
          <EmailVerifyBanner />
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 md:py-8 pb-24 sm:pb-8">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={reduceMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
              transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
              className="mx-auto max-w-[1200px] space-y-6 md:space-y-8"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Etter opplåsing med gjenopprettingskode: velg nytt hovedpassord + ny kode (felles for alle Passord-sider) */}
        <RecoveryFollowUpDialog />

        {/* Animert gjennomgang (første gang / fra Hjelp) */}
        <WalkthroughDialog />

        {!hideFab && (
          <Link to={primaryAction.to} className="sm:hidden fixed bottom-5 right-5 z-30 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center" aria-label={primaryAction.label} data-testid="mobile-primary-fab">
            <primaryAction.icon className="h-6 w-6" />
          </Link>
        )}
      </div>
    </div>
  );
};

export const Breadcrumb = ({ items }) => (
  <nav className="flex items-center gap-1 text-sm text-muted-foreground" aria-label="Brødsmuler">
    {items.map((it, i) => (
      <React.Fragment key={i}>
        {i > 0 ? <ChevronRight className="h-3.5 w-3.5" /> : null}
        {it.to ? <Link to={it.to} className="hover:text-foreground">{it.label}</Link> : <span className="text-foreground">{it.label}</span>}
      </React.Fragment>
    ))}
  </nav>
);
